import React, { useEffect, useState } from 'react';
import * as S from './HistoryPractice.styles';
import { Modal } from '@app/components/common/Modal/Modal';
import { TableCollapse } from './TableCollapse';
import { TableProps, Tag } from 'antd';
import { IHistoryPractice, practiceDelete, practiceHistory, practiceScoreAI } from '@app/api/academic.api';
import { notificationController } from '@app/controllers/notificationController';
import { useSelector } from 'react-redux';
import { RootState } from '@app/store/store';
import { Collapse } from 'antd';
import { Button } from 'antd';
import { statusTypePractice } from '@app/utils/utils';
import { useTranslation } from 'react-i18next';
import { Pagination } from '@app/components/common/Pagination/Pagination';

interface IHistoryPracticeProps {
  type: string;
  reload: boolean;
}

export const HistoryPractice: React.FC<IHistoryPracticeProps> = ({ type, reload }) => {
  const { t } = useTranslation();
  const { currentQuestion } = useSelector((state: RootState) => state.exam);
  const [columns, setColumns] = useState<TableProps<IHistoryPractice>['columns']>([
    { title: '#', dataIndex: 'index', key: 'index', render: (_: any, __: any, index: number) => index + 1 },
    {
      title: t('pteSection.history.order'),
      dataIndex: 'order_id',
      key: 'order_id',
    },
    {
      title: t('pteSection.history.date'),
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: t('pteSection.history.time'),
      dataIndex: 'time',
      key: 'time',
      render: (time: number) =>
        `${Math.floor(time / 60)
          .toString()
          .padStart(2, '0')}:${(time % 60).toString().padStart(2, '0')}`,
    },
    {
      title: t('pteSection.history.totalScore'),
      dataIndex: 'score',
      key: 'score',
      render: (score: IHistoryPractice) => {
        return score?.score?.total ?? '0';
      },
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IHistoryPractice[]>([]);
  const [activeKey, setActiveKey] = useState<string | string[]>('');
  const [isModalDelete, setIsModalDelete] = useState<boolean>(false);
  const [currentIdDetail, setCurrentIdDetail] = useState<string>('');

  const [pagiInfo, setPagiInfo] = useState({
    page: 1,
    pageSize: 10,
    totalItem: 0,
    totalPage: 0,
  });

  useEffect(() => {
    setActiveKey('');
    if (type.startsWith('SPEAKING') || type.startsWith('WRITING')) {
      setColumns((prevColumns = []) => [
        ...prevColumns,
        {
          title: t('pteSection.history.status'),
          dataIndex: 'status',
          key: 'status',
          render: (status: string) => (
            <Tag color={statusTypePractice[status]} key={status}>
              {t(`pteSection.history.statusType.${status}`)}
            </Tag>
          ),
        },
        {
          title: t('pteSection.history.action'),
          dataIndex: 'status',
          render: (status: string, record: IHistoryPractice) => (
            <div className="flex gap-2">
              {status === 'NOT_SCORED' && (
                <Button
                  size="small"
                  onClick={() => {
                    fetchScoreAI(record.id);
                  }}
                >
                  {t('pteSection.history.score')}
                </Button>
              )}
              {status === 'ERROR' && (
                <Button
                  size="small"
                  onClick={() => {
                    fetchScoreAI(record.id);
                  }}
                >
                  {t('pteSection.history.reScore')}
                </Button>
              )}
              {status !== 'SCORING' && (
                <Button
                  size="small"
                  onClick={() => {
                    handleModalDeleteClick(record.id);
                  }}
                >
                  {t('pteSection.history.delete')}
                </Button>
              )}
            </div>
          ),
        },
      ]);
    } else {
      setColumns((prevColumns = []) => [
        ...prevColumns,
        {
          title: t('pteSection.history.action'),
          dataIndex: 'status',
          render: (status: string, record: IHistoryPractice) => (
            <div className="flex gap-2">
              <Button
                size="small"
                onClick={() => {
                  handleModalDeleteClick(record.id);
                }}
              >
                {t('pteSection.history.delete')}
              </Button>
            </div>
          ),
        },
      ]);
    }
    fetchPracticeResult();
  }, [currentQuestion?.id]);

  useEffect(() => {
    fetchPracticeResult();
  }, [pagiInfo.page]);

  useEffect(() => {
    if (!reload) return;
    setTimeout(() => {
      fetchPracticeResult();
      setActiveKey('1');
    }, 2000);
  }, [reload]);

  const fetchPracticeResult = async () => {
    if (!currentQuestion?.id) return;
    try {
      setLoading(true);

      const { data, meta } = await practiceHistory(currentQuestion?.id, pagiInfo.page, pagiInfo.pageSize);
      setData(data);
      setPagiInfo({
        page: meta.page,
        pageSize: pagiInfo.pageSize,
        totalItem: meta.itemCount,
        totalPage: meta.pageCount,
      });
    } catch (error) {
      notificationController.error({
        message: error,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchScoreAI = async (userPracticeId: string) => {
    try {
      setLoading(true);
      const res = await practiceScoreAI(userPracticeId);
      if (res.statusCode === 200) {
        fetchPracticeResult();
      }
    } catch (error) {
      notificationController.error({
        message: error,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleModalDeleteClick = (id: string) => {
    setCurrentIdDetail(id);
    setIsModalDelete(true);
  };

  const handleDeleteClick = async () => {
    try {
      if (!currentIdDetail) return;

      const res = await practiceDelete(currentIdDetail);
      if (res.statusCode == 200) {
        notificationController.success({ message: `Delete practice user success` });
        fetchPracticeResult();
      }
    } catch (error) {
      notificationController.error({
        message: error,
      });
    } finally {
      setIsModalDelete(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= pagiInfo.totalPage) {
      setPagiInfo({
        ...pagiInfo,
        page: newPage,
      });
    }
  };

  return (
    <S.CardHistory>
      <Collapse activeKey={activeKey} onChange={setActiveKey}>
        <Collapse.Panel header={t('common.showResult')} key="1">
          <S.CardHistoryBody>
            <TableCollapse columns={columns} dataSource={data} />

            <Pagination
              className="text-center mt-5"
              showSizeChanger={false}
              defaultCurrent={pagiInfo.page}
              total={pagiInfo.totalItem}
              pageSize={pagiInfo.pageSize}
              onChange={handlePageChange}
            />
          </S.CardHistoryBody>
        </Collapse.Panel>
      </Collapse>

      {/* Modal confirm delete */}
      <Modal
        title={t('pteSection.history.modal.title')}
        visible={isModalDelete}
        onOk={handleDeleteClick}
        onCancel={() => setIsModalDelete(false)}
        okText={t('pteSection.history.modal.delete')}
        cancelText={t('pteSection.history.modal.cancel')}
      >
        <p>{t('pteSection.history.modal.content')}</p>
        <p>{t('pteSection.history.modal.content1')}</p>
      </Modal>
    </S.CardHistory>
  );
};
